<template>
  <div class="purchase-register">
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Supplier</label>
          <FormDropdown v-model="filters.supplier" :options="supplierOptions" placeholder="All suppliers" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Invoice</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control"
                   placeholder="Search invoice, chalan or supplier" />
          </div>
        </div>
      </div>

      <ReportActions :loading="loading" :exporting="exporting" :can-reset="hasActiveFilters" :can-print="!!rows.length"
                     @reset="resetFilters" @export="exportFile" @print="printReport" />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-file-invoice"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Total Purchases</div>
          <div class="rp-card-value">{{ formatMoney(summary.total) }}</div>
          <div class="rp-card-sub">{{ plural(summary.invoice_count, 'invoice') }} · avg {{ formatMoney(summary.avg_invoice) }}</div>
        </div>
      </div>
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-packages"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Quantity · VAT</div>
          <div class="rp-card-value">{{ formatQty(summary.qty) }}</div>
          <div class="rp-card-sub">
            units from {{ plural(summary.supplier_count, 'supplier') }} · VAT {{ formatMoney(summary.vat) }}
          </div>
        </div>
      </div>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-truck-return"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Returned · Net</div>
          <div class="rp-card-value">{{ formatMoney(summary.net_amount) }}</div>
          <div class="rp-card-sub">
            after {{ formatMoney(summary.returned_amount) }} returned on {{ plural(summary.returned_count, 'invoice') }}
          </div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: dueSelected }"
              :title="dueSelected ? 'Show all invoices' : 'Show invoices still owed'" @click="toggleDue">
        <span class="rp-card-icon"><i class="ti ti-clock-dollar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Paid · Due</div>
          <div class="rp-card-value" :class="{ 'text-danger': Number(summary.due) }">{{ formatMoney(summary.due) }}</div>
          <div class="rp-card-sub">
            due on {{ plural(summary.due_count, 'invoice') }} · {{ formatMoney(summary.paid) }} paid ({{ summary.paid_rate }}%)
          </div>
        </div>
      </button>
    </div>

    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Purchases {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: PURCHASE_COLOR }"></i>Purchases</span>
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Invoices</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No purchases in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Payment status</div>
        <div class="rp-bars">
          <div v-for="part in statusBars" :key="part.key">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ part.label }}</span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(part.value) }} <span class="text-muted fw-normal">({{ part.share }}%)</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="part.tone" :style="{ width: `${part.share}%` }"></div></div>
          </div>
        </div>
        <div class="rp-note mt-3">
          Payments clear each supplier's oldest bills first, so paid / due show where each invoice stands today.
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Top suppliers</div>
        <ul v-if="summary.top_suppliers.length" class="rp-ranked">
          <li v-for="(supplier, index) in summary.top_suppliers" :key="supplier.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="supplier.name">{{ supplier.name }}</div>
              <small class="text-muted">
                {{ plural(supplier.count, 'invoice') }}<template v-if="Number(supplier.due)"> · {{ formatMoney(supplier.due) }} due</template>
              </small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(supplier.total) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No purchases in this period.</p>
      </div>
    </div>

    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in statusPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.status || 'all') === pill.key }" @click="setStatus(pill.key)">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}<i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button type="button" class="rp-expand" :title="expanded[row.key] ? 'Hide items' : 'Show items'"
                          @click="toggleRow(row.key)">
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="text-nowrap">
                  {{ formatDate(row.date) }}<span v-if="row.time" class="rp-sub">{{ row.time }}</span>
                </td>
                <td>
                  <div class="fw-semibold text-nowrap">{{ row.invoice_no }}</div>
                  <span v-if="row.chalan_no" class="rp-sub">Chalan {{ row.chalan_no }}</span>
                </td>
                <td>
                  <div class="fw-medium">{{ row.supplier }}</div>
                  <span v-if="row.supplier_phone" class="rp-sub">{{ row.supplier_phone }}</span>
                </td>
                <td class="text-center">
                  <span class="rp-count" :title="`${formatQty(row.qty)} units`">{{ row.item_count }}</span>
                  <span class="rp-sub">{{ formatQty(row.qty) }} units</span>
                </td>
                <td class="text-end">{{ formatMoney(row.cost) }}</td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.discount) }">
                  {{ Number(row.discount) ? `−${formatMoney(row.discount)}` : '—' }}
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.vat) }">
                  {{ Number(row.vat) ? formatMoney(row.vat) : '—' }}
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.total) }}</td>
                <td class="text-end" :class="Number(row.returned_amount) ? 'text-info' : 'text-muted'">
                  {{ Number(row.returned_amount) ? `−${formatMoney(row.returned_amount)}` : '—' }}
                </td>
                <td class="text-end" :class="Number(row.due) ? 'text-danger fw-semibold' : 'text-muted'">
                  {{ Number(row.due) ? formatMoney(row.due) : '—' }}
                  <span v-if="row.age_days !== null" class="rp-sub">{{ plural(row.age_days, 'day') }} old</span>
                </td>
                <td>
                  <span class="rp-badge" :class="STATUS_TONES[row.payment_status]">{{ row.payment_label }}</span>
                  <span v-if="row.return_status" class="rp-badge info d-table mt-1">
                    <i class="ti ti-arrow-back-up"></i>{{ row.return_label }}
                  </span>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title d-flex flex-wrap justify-content-between gap-2">
                      <span><i class="ti ti-file-invoice me-1"></i>Invoice {{ row.invoice_no }}</span>
                      <span class="text-muted fw-normal">
                        Received by {{ row.created_by || '—' }}<template v-if="row.warehouse"> · {{ row.warehouse }}</template>
                      </span>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>Variant</th>
                            <th>Batch</th>
                            <th>Expiry</th>
                            <th class="text-end">Qty</th>
                            <th class="text-end">Unit Price</th>
                            <th class="text-end">Discount</th>
                            <th class="text-end">VAT</th>
                            <th class="text-end">Total</th>
                            <th class="text-end">Returned</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="line in row.lines" :key="line.key">
                            <td class="fw-semibold">{{ line.item_code }}</td>
                            <td>
                              {{ line.item_name }}
                              <small v-if="line.sku" class="d-block text-muted">SKU: {{ line.sku }}</small>
                            </td>
                            <td>
                              <template v-if="line.variant">
                                <span v-for="part in line.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                              </template>
                              <span v-else class="text-muted">—</span>
                            </td>
                            <td>{{ line.batch_no || '—' }}</td>
                            <td class="text-nowrap">{{ formatDate(line.expiry_date) }}</td>
                            <td class="text-end fw-semibold">
                              {{ formatQty(line.qty) }}<small v-if="line.unit" class="text-muted ms-1">{{ line.unit }}</small>
                            </td>
                            <td class="text-end">{{ formatMoney(line.unit_price) }}</td>
                            <td class="text-end">{{ Number(line.discount) ? `−${formatMoney(line.discount)}` : '—' }}</td>
                            <td class="text-end">{{ Number(line.vat) ? formatMoney(line.vat) : '—' }}</td>
                            <td class="text-end fw-semibold">{{ formatMoney(line.total) }}</td>
                            <td class="text-end" :class="{ 'text-info': Number(line.returned_qty) }">
                              {{ Number(line.returned_qty) ? formatQty(line.returned_qty) : '—' }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="pr-totals">
                      <div v-for="item in invoiceTotals(row)" :key="item.label" :class="item.class">
                        <span>{{ item.label }}</span><b>{{ item.value }}</b>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-file-invoice"></i>
                  <div class="fw-semibold">No purchase invoices found</div>
                  <small class="text-muted">Try another period, supplier or status.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th colspan="3" class="text-end">Total ({{ plural(rows.length, 'invoice') }})</th>
              <th class="text-center">{{ formatQty(summary.qty) }}</th>
              <th class="text-end">{{ formatMoney(summary.cost) }}</th>
              <th class="text-end">{{ Number(summary.discount) ? `−${formatMoney(summary.discount)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.vat) }}</th>
              <th class="text-end">{{ formatMoney(summary.total) }}</th>
              <th class="text-end">{{ Number(summary.returned_amount) ? `−${formatMoney(summary.returned_amount)}` : '—' }}</th>
              <th class="text-end text-danger">{{ formatMoney(summary.due) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination v-if="visibleRows.length" :page="page" :page-count="pageCount" :pages="pageNumbers"
                        :start="pageStart" :end="pageEnd" :total="visibleRows.length" @go="goToPage" />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Total = cost − discount + VAT, as entered on the purchase. Supplier payments aren't tied to an invoice: they
      clear the supplier's oldest bills first, so paid and due are where each invoice stands today.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { SALES_COLOR } from '../sales/salesReportMixin';
import purchaseReport, { PURCHASE_COLOR, STATUS_TONES } from './purchaseReportMixin';

const EMPTY_SUMMARY = {
  qty: 0, cost: 0, discount: 0, vat: 0, total: 0, returned_qty: 0, returned_amount: 0, net_amount: 0, paid: 0,
  due: 0, invoice_count: 0, supplier_count: 0, avg_invoice: 0, paid_rate: 0, due_count: 0, returned_count: 0,
  status_counts: { all: 0, paid: 0, partial: 0, unpaid: 0, returned: 0 }, top_suppliers: [], trend_unit: 'day',
  trend: [],
};

export default {
  name: 'PurchaseRegisterReport',
  mixins: [inventoryReport, periodReport, salesReport, purchaseReport],

  reportUrl: apiUrls.purchase_register_report.get,
  fileName: 'purchase_register',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'invoice_no', label: 'Invoice' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'item_count', label: 'Items', class: 'text-center', numeric: true },
    { key: 'cost', label: 'Cost', class: 'text-end', numeric: true },
    { key: 'discount', label: 'Discount', class: 'text-end', numeric: true },
    { key: 'vat', label: 'VAT', class: 'text-end', numeric: true },
    { key: 'total', label: 'Total', class: 'text-end', numeric: true },
    { key: 'returned_amount', label: 'Returned', class: 'text-end', numeric: true },
    { key: 'due', label: 'Due', class: 'text-end', numeric: true },
    { key: 'payment_label', label: 'Status' },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), supplier: null, status: null, warehouse: null, category: null, sub_category: null,
        search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      PURCHASE_COLOR,
      SALES_COLOR,
      STATUS_TONES,
    };
  },

  computed: {
    hasActiveFilters() {
      const { supplier, status, search } = this.filters;
      return this.periodChanged || !!(supplier || status || search.trim());
    },

    statusPills() {
      const counts = this.summary.status_counts;
      return [
        { key: 'all', label: 'All invoices', count: counts.all },
        { key: 'paid', label: 'Paid', count: counts.paid },
        { key: 'partial', label: 'Partly paid', count: counts.partial },
        { key: 'unpaid', label: 'Unpaid', count: counts.unpaid },
        { key: 'returned', label: 'With returns', count: counts.returned },
      ];
    },

    statusBars() {
      const s = this.summary;
      const total = (Number(s.paid) || 0) + (Number(s.due) || 0) + (Number(s.returned_amount) || 0);
      const share = (v) => (total ? Math.round(((Number(v) || 0) / total) * 100) : 0);
      return [
        { key: 'paid', label: 'Paid', value: s.paid, share: share(s.paid), tone: 'success' },
        { key: 'returned', label: 'Returned to supplier', value: s.returned_amount, share: share(s.returned_amount), tone: 'info' },
        { key: 'due', label: 'Still due', value: s.due, share: share(s.due), tone: 'danger' },
      ];
    },

    dueSelected() {
      return this.filters.status === 'partial' || this.filters.status === 'unpaid';
    },

    visibleRows() {
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.status'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { status } = this.filters;
      return { ...this.periodParams(), ...this.supplierParams(), ...(status ? { status } : {}) };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), supplier: null, status: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Purchases', field: 'total', color: PURCHASE_COLOR },
        { label: 'Invoices', field: 'count', color: SALES_COLOR, count: true },
      ]));
    },

    setStatus(key) {
      this.filters.status = key === 'all' ? null : key;
    },

    toggleDue() {
      this.filters.status = this.dueSelected ? null : 'unpaid';
    },

    invoiceTotals(row) {
      const items = [
        { label: 'Cost', value: this.formatMoney(row.cost) },
        ...(Number(row.discount) ? [{ label: 'Discount', value: `−${this.formatMoney(row.discount)}` }] : []),
        ...(Number(row.vat) ? [{ label: 'VAT', value: this.formatMoney(row.vat) }] : []),
        { label: 'Total', value: this.formatMoney(row.total), class: 'is-total' },
      ];
      if (Number(row.returned_amount)) {
        items.push({ label: `Returned (${this.formatQty(row.returned_qty)})`, value: `−${this.formatMoney(row.returned_amount)}`, class: 'text-info' });
      }
      items.push({ label: 'Paid', value: this.formatMoney(row.paid) });
      items.push({ label: 'Due', value: this.formatMoney(row.due), class: Number(row.due) ? 'text-danger' : '' });
      return items;
    },

    printReport() {
      const s = this.summary;
      const status = this.statusPills.find((p) => p.key === (this.filters.status || 'all'))?.label;
      const search = this.filters.search.trim();
      this.printColumns(
        'Purchase Register',
        [this.periodText(), `Status: ${status}`, this.supplierText(), this.storeText(), search && `Search: ${search}`],
        [
          ['Total Purchases', `${this.formatMoney(s.total)} · ${this.plural(s.invoice_count, 'invoice')}`],
          ['Quantity', this.formatQty(s.qty)],
          ['Returned · Net', `${this.formatMoney(s.returned_amount)} · ${this.formatMoney(s.net_amount)}`],
          ['Paid · Due', `${this.formatMoney(s.paid)} · ${this.formatMoney(s.due)}`],
        ],
        [
          { label: 'Date', value: (r) => `${this.formatDate(r.date)} ${r.time}` },
          { label: 'Invoice', value: (r) => r.invoice_no },
          { label: 'Supplier', value: (r) => r.supplier },
          { label: 'Chalan', value: (r) => r.chalan_no },
          { label: 'Qty', value: (r) => this.formatQty(r.qty), num: true },
          { label: 'Cost', value: (r) => this.formatMoney(r.cost), num: true },
          { label: 'Discount', value: (r) => this.formatMoney(r.discount), num: true },
          { label: 'VAT', value: (r) => this.formatMoney(r.vat), num: true },
          { label: 'Total', value: (r) => this.formatMoney(r.total), num: true },
          { label: 'Returned', value: (r) => this.formatMoney(r.returned_amount), num: true },
          { label: 'Due', value: (r) => this.formatMoney(r.due), num: true },
          { label: 'Status', value: (r) => r.payment_label + (r.return_label ? ` · ${r.return_label}` : '') },
        ],
        this.visibleRows,
        {
          Chalan: 'Total', Qty: this.formatQty(s.qty), Cost: this.formatMoney(s.cost),
          Discount: this.formatMoney(s.discount), VAT: this.formatMoney(s.vat), Total: this.formatMoney(s.total),
          Returned: this.formatMoney(s.returned_amount), Due: this.formatMoney(s.due),
        },
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.pr-totals {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem 1.5rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px dashed #EEF0F3;
  font-size: 0.8rem;
}

.pr-totals > div {
  display: flex;
  gap: 0.5rem;
  color: #646B72;
}

.pr-totals b {
  color: #092C4C;
}

.pr-totals .is-total b {
  color: #D9771E;
}

.pr-totals .text-danger b,
.pr-totals .text-info b {
  color: inherit;
}
</style>
